<div class="welcome-panel" [attr.aria-busy]="loading()" aria-labelledby="setup-title">
  <div class="welcome-content" [attr.inert]="loading() ? '' : null">
    <header class="welcome-header">
      <img class="welcome-logo" [src]="logo()?.url" draggable="false" alt="Rooms" />
      <div>
        <h1 id="setup-title">{{ 'Welcome to Rooms' | translate }}</h1>
        <p>{{ 'Make room for your team.' | translate }}</p>
      </div>
    </header>

    <div class="identity-fields">
      <div>
        <andor-text-input
          [control]="appTitle"
          [label]="'App name' | translate"
          [errorMsg]="'This field must not have more than 64 chars.' | translate"
          placeholder="Rooms" />
        @if (titleInvalid()) {
          <p class="field-error" role="alert">
            {{ 'This field must not have more than 64 chars.' | translate }}
          </p>
        }
      </div>
      <andor-region-select
        [host]="gatewayHost()"
        (hostChange)="selectRegion($event)"
        [required]="true"
        [disabled]="loading()"
        [hint]="'Choose the region closest to your team.' | translate" />
    </div>

    <div class="setup-columns">
      <andor-theme-editor
        class="theme-section"
        role="region"
        aria-labelledby="theme-title"
        [disabled]="loading()">
        <h2 editor-heading id="theme-title">{{ 'Choose your look' | translate }}</h2>
      </andor-theme-editor>

      <div class="details-column">
        <section class="rooms-section" aria-labelledby="rooms-title">
          <div class="section-heading">
            <h2 id="rooms-title">{{ 'Your rooms' | translate }}</h2>
            <button
              class="text-button"
              type="button"
              (click)="addRoom()"
              [disabled]="draftRooms().length >= 50">
              <fa-duotone-icon [icon]="faPlus" />
              {{ 'Add room' | translate }}
            </button>
          </div>
          <p class="section-description">
            {{ 'Start with a Lobby, or make a space of your own.' | translate }}
          </p>
          <div class="room-list">
            @for (room of draftRooms(); track room.tempId) {
              <div class="room-tile">
                <span class="room-glyph" aria-hidden="true"><span></span></span>
                <div class="room-info">
                  <span class="room-name" [title]="room.name">{{ room.name }}</span>
                  <span class="room-meta">
                    {{ (room.isPrivate ? 'Private' : 'Public') | translate }}
                    @if (room.password) {
                      <fa-icon
                        [icon]="faKey"
                        [andorTooltip]="'Password protected' | translate"
                        [attr.aria-label]="'Password protected' | translate" />
                    }
                    @if (room.schedule) {
                      <fa-icon
                        [icon]="faClock"
                        [andorTooltip]="scheduleTooltip(room.schedule)"
                        [attr.aria-label]="scheduleTooltip(room.schedule)" />
                    }
                  </span>
                </div>
                <button
                  type="button"
                  class="remove-room"
                  (click)="removeRoom(room.tempId)"
                  [attr.aria-label]="('Remove room' | translate) + ': ' + room.name"
                  [andorTooltip]="'Remove room' | translate">
                  <fa-duotone-icon [icon]="faTrashCan" />
                </button>
              </div>
            }
          </div>
          @if (!draftRooms().length) {
            <p class="field-error" role="status">
              {{ 'Add at least one room to finish setup.' | translate }}
            </p>
          } @else if (draftRooms().length >= 50) {
            <p class="field-note">
              {{ 'You can create up to 50 rooms during setup.' | translate }}
            </p>
          }
        </section>

        <section
          class="owner-section"
          aria-labelledby="owner-title"
          (keydown.enter)="onPasswordEnter($event)">
          <h2 id="owner-title">{{ 'Owner access' | translate }}</h2>
          <p class="section-description">
            {{ 'Keep this password safe. It gives full access to your app.' | translate }}
          </p>
          <andor-password-confirm-inputs
            [password]="adminPassword"
            (changed)="adminPassword = $event"
            (valid)="adminPasswordValid.set($event)"
            [labelPassword]="'Owner password' | translate"
            [labelConfirmPassword]="'Confirm password' | translate" />
        </section>
      </div>
    </div>
  </div>

  <footer class="setup-footer">
    <div class="footer-message" aria-live="polite">
      @for (error of errors(); track error) {
        <p class="field-error" role="alert">{{ error | translate }}</p>
      }
      @if (cooldown.remaining('setup')) {
        <p role="status">
          {{
            'Please wait [seconds] seconds before trying again.'
              | translate: { seconds: cooldown.remaining('setup') }
          }}
        </p>
      }
      @if (!errors().length) {
        <p>{{ 'You can change these settings later.' | translate }}</p>
      }
    </div>
    <andor-button
      type="gradient"
      [disabled]="!canFinish() || loading() || cooldown.remaining('setup') > 0"
      (click)="finalize()">
      @if (loading()) {
        <span class="saving-indicator" aria-hidden="true"></span>
      }
      <span>{{ (loading() ? 'Setting up…' : 'Finish setup') | translate }}</span>
    </andor-button>
  </footer>
</div>
